/**
 * HOOK: useFormSubmissions
 * Gestion des soumissions de formulaires
 */

import { useEffect, useCallback } from 'react';
import { useCRUD } from './useCRUD';
import type { FormSubmission, CreateFormSubmissionDto } from '../types/form';
import { useSupabase } from './useSupabase';

interface UseFormSubmissionsOptions {
  formId?: string;
  autoLoad?: boolean;
}

export function useFormSubmissions(options: UseFormSubmissionsOptions = {}) {
  const { formId, autoLoad = true } = options;
  const supabase = useSupabase();

  const crud = useCRUD<FormSubmission, CreateFormSubmissionDto>('form_submissions', {
    select: `
      *,
      form:forms(id, name, slug),
      contact:contacts(id, first_name, last_name, email)
    `,
    orderBy: { column: 'created_at', ascending: false },
    manual: !autoLoad || !formId,
  });

  // Recharger quand formId change
  useEffect(() => {
    if (formId && autoLoad) {
      crud.refresh();
    }
  }, [formId, autoLoad, crud]);

  /**
   * Charge les soumissions d'un formulaire spécifique
   */
  const loadSubmissionsForForm = useCallback(
    async (formId: string) => {
      const { data, error } = await supabase
        .from('form_submissions')
        .select(
          `
          *,
          form:forms(id, name, slug),
          contact:contacts(id, first_name, last_name, email)
        `
        )
        .eq('form_id', formId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading form submissions:', error);
        return [];
      }

      return data || [];
    },
    [supabase]
  );

  /**
   * Crée une soumission et tente de créer/associer un contact
   */
  const submitForm = useCallback(
    async (dto: CreateFormSubmissionDto): Promise<FormSubmission | null> => {
      try {
        // Créer la soumission
        const submission = await crud.create(dto);

        if (!submission) {
          return null;
        }

        // Si les données contiennent un email, créer/associer un contact
        const email = dto.data.email;
        if (email && typeof email === 'string') {
          // Appeler la fonction PostgreSQL pour créer/récupérer le contact
          const { data: contactResult, error: contactError } = await supabase.rpc(
            'create_contact_from_submission',
            {
              submission_data: dto.data,
              form_source: null, // TODO: Récupérer la source depuis le formulaire
            }
          );

          if (!contactError && contactResult) {
            // Mettre à jour la soumission avec l'ID du contact
            await supabase
              .from('form_submissions')
              .update({ contact_id: contactResult })
              .eq('id', submission.id);
          }
        }

        return submission as FormSubmission;
      } catch (error) {
        console.error('Error submitting form:', error);
        return null;
      }
    },
    [crud, supabase]
  );

  /**
   * Récupère les statistiques d'un formulaire
   */
  const getFormStats = useCallback(
    async (formId: string) => {
      const { count, error } = await supabase
        .from('form_submissions')
        .select('*', { count: 'exact', head: true })
        .eq('form_id', formId);

      if (error) {
        console.error('Error getting form stats:', error);
        return { total: 0 };
      }

      return { total: count || 0 };
    },
    [supabase]
  );

  return {
    // Données
    submissions: crud.data,
    loading: crud.loading,
    error: crud.error,

    // Opérations CRUD
    createSubmission: crud.create,
    refreshSubmissions: crud.refresh,

    // Opérations spécifiques
    loadSubmissionsForForm,
    submitForm,
    getFormStats,
  };
}
